import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getTotals, getTotalsByTagLevels } from "../../docuclipper/api";
import { fetchJobDocuments } from "./JobDocument";
import { setAccountChunks, setAccounts, setSelectedAccount, setSelectedChunk } from "./TransactionManager";
import { ReduxState } from "..";
import { isNullOrUndefined } from "../../utils/utils";
import { uniq, flatten } from 'lodash';
import { ChunkData, MetadataField } from "src/docuclipper/DocuclipperTypes";


const initialState: {
  totals: {};
  totalsByDocumentId: {};
  totalsByChunk: Record<string, Record<string, Record<string, ChunkData>>>;
  badTransactionsByDocumentId: {};
  byDayByDocumentId: { reconciled: string[]; unreconciled: string[] };
  loading: boolean;
  error: string | null;
  allAccounts: string[];
  selectedAccountReconciler: string | null; // used for allaccounts dropdowns in fraud detection, categorization etc
} = {
  totals: {},
  totalsByDocumentId: {},
  totalsByChunk: {},
  badTransactionsByDocumentId: {},
  byDayByDocumentId: { reconciled: [], unreconciled: [] },
  loading: false,
  error: null,
  allAccounts: [],
  selectedAccountReconciler: '__ALL__'
};

const slice = createSlice({
  name: "Reconciler",
  initialState,
  reducers: {
    load(state) {
      state.loading = true;
      state.error = null;
    },
    setTotals(state, action: PayloadAction<{}>) {
      state.loading = false;
      state.totals = action.payload;
    },
    setSelectedAccountReconciler(state, action: PayloadAction<string>) {
      state.selectedAccountReconciler = action.payload;

    },
    setTotalsByDocumentId(state, action: PayloadAction<{}>) {
      state.loading = false;
      state.totalsByDocumentId = action.payload;
      const docIds = Object.keys(action.payload || {});
      let accounts: any[] = [];
      for (const docId of docIds) {
        accounts.push(Object.keys((action.payload || {})[docId]));
      }

      state.allAccounts = uniq(flatten(accounts));

    },
    setTotalsByChunk(state, action: PayloadAction<{}>) {
      state.loading = false;
      state.totalsByChunk = action.payload;
    },
    setBadTransactionsByDocumentId(state, action: PayloadAction<{}>) {
      state.loading = false;
      state.badTransactionsByDocumentId = action.payload;
    },
    setByDayByDocumentId(
      state,
      action: PayloadAction<{ reconciled: string[]; unreconciled: string[] }>
    ) {
      state.loading = false;
      state.byDayByDocumentId = action.payload;
    },
    setError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  load,
  setError,
  setTotals,
  setTotalsByDocumentId,
  setTotalsByChunk,
  setBadTransactionsByDocumentId,
  setByDayByDocumentId,
  setSelectedAccountReconciler
} = slice.actions;

export const fetchTotals = ({
  account,
  chunk,
}: {
  account?: string,
  chunk?: string,
} = {}) => async (dispatch, getState) => {
  const state: ReduxState = getState();

  const { files } = state.Files;
  const cleanCsvDocumentId =
    files.length > 0 &&
      files[0].document &&
      files[0].document.mimetype ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ? files[0].document.id
      : null;

  const { loading } = state.Reconciler;
  const { selectedL1, selectedL2 } = state.TagLevel;
  if (loading) {
    return;
  }

  const jobId2 = state.JobData.job?.id;
  if (!jobId2 && !selectedL1 && !selectedL2) {
    return;
  }

  dispatch(load());
  try {
    let data;
    if (selectedL1 && selectedL2) {
      data = await getTotalsByTagLevels(selectedL1, selectedL2); // You'll need to implement this API function
    } else if (jobId2) {
      data = await getTotals(jobId2 as any, cleanCsvDocumentId);
    }

    dispatch(setTotals(data.totals));
    dispatch(setTotalsByDocumentId(data.totalsByDocumentId || {}));
    dispatch(setTotalsByChunk(data.totalsByChunk || {}));
    dispatch(setBadTransactionsByDocumentId(data.badTransactionsByDocumentId));
    dispatch(setByDayByDocumentId(data.byDayByDocumentId));

    if (account && chunk) {
      dispatch(setSelectedAccount(account));
      dispatch(setSelectedChunk(chunk));

    }

  } catch (err) {
    dispatch(setError((err as any)?.message || "Error getting totals"));
  }
};

export default slice.reducer;
